/**
 * The level line (rubber band) of a sound clip on the timeline (1.7.4): where a level is drawn in the clip's box and
 * back, the line's points, and the dB readout. Pure.
 *
 * The scale is piecewise linear, so the levels people use most get the most room: +12…0 dB in the top fifth, 0…−24 dB
 * in the next three fifths, −24…−60 dB (silence) in the last fifth.
 */
import { MAX_DB, MIN_DB, TICKS_PER_SECOND, levelPoints, type VolumeKey } from '@prism/contracts';

const SCALE: Array<[db: number, y: number]> = [[MAX_DB, 0], [0, 0.2], [-24, 0.8], [MIN_DB, 1]];

/** A level's height in a box `h` pixels tall (0 = top). */
export function dbToY(db: number, h: number): number {
  const d = Math.min(MAX_DB, Math.max(MIN_DB, db));
  for (let i = 1; i < SCALE.length; i++) {
    const [d0, y0] = SCALE[i - 1]!;
    const [d1, y1] = SCALE[i]!;
    if (d >= d1) return (y0 + ((d0 - d) / (d0 - d1)) * (y1 - y0)) * h;
  }
  return h;
}

/** The level at a height in a box `h` pixels tall (clamped to the box), to 0.1 dB. */
export function yToDb(y: number, h: number): number {
  const f = h > 0 ? Math.min(1, Math.max(0, y / h)) : 0;
  for (let i = 1; i < SCALE.length; i++) {
    const [d0, y0] = SCALE[i - 1]!;
    const [d1, y1] = SCALE[i]!;
    if (f <= y1) return Math.round((d0 + ((f - y0) / (y1 - y0)) * (d1 - d0)) * 10) / 10;
  }
  return MIN_DB;
}

/** A level as the readout shows it: one decimal, a true minus sign, "−∞ dB" at silence. */
export function formatDb(db: number): string {
  if (db <= MIN_DB) return '−∞ dB';
  const r = Math.round(db * 10) / 10;
  return `${r < 0 ? '−' : r > 0 ? '+' : ''}${Math.abs(r).toFixed(1)} dB`;
}

/**
 * The line's SVG points in the clip's box: x in pixels from the clip's left edge (source time from its In at `pps`
 * pixels per second), y from dbToY.
 */
export function levelPath(clip: { gainDb: number; volumeKeys?: readonly VolumeKey[]; sourceInTicks: number; sourceOutTicks: number }, pps: number, h: number): string {
  return levelPoints(clip).map((p) => `${(((p.atTicks - clip.sourceInTicks) / TICKS_PER_SECOND) * pps).toFixed(1)},${dbToY(p.db, h).toFixed(1)}`).join(' ');
}

/** A source time for a pointer `px` pixels from the clip's left edge. */
export const sourceTicksAt = (clip: { sourceInTicks: number }, px: number, pps: number): number => clip.sourceInTicks + (px / pps) * TICKS_PER_SECOND;
